import { z } from 'zod'

export const edyStatisticsTabs = ['users', 'interns', 'organizations', 'supportCases'] as const

const edyStatisticsViews = ['daily', 'monthly', 'hourly'] as const

export type EdyStatisticsTab = (typeof edyStatisticsTabs)[number]

type EdyStatisticsView = (typeof edyStatisticsViews)[number]

export const edyStatisticsTabViews: Record<EdyStatisticsTab, readonly EdyStatisticsView[]> = {
  users: edyStatisticsViews,
  interns: edyStatisticsViews,
  organizations: ['monthly'],
  supportCases: ['daily'],
}

const statisticsStartYear = 2025

export const hourlyStatisticsStartDate = '2025-06-06'

export const edyStatisticsSearchSchema = z.object({
  tab: z.enum(edyStatisticsTabs).catch('users'),
  view: z.enum(edyStatisticsViews).catch('daily'),
  year: z.coerce.number().int().min(statisticsStartYear).optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
  date: z.iso.date().optional().catch(undefined),
})

export type EdyStatisticsSearch = z.infer<typeof edyStatisticsSearchSchema>

export const defaultEdyStatisticsSearch: EdyStatisticsSearch = {
  tab: 'users',
  view: 'daily',
}

export const resolveStatisticsView = (search: EdyStatisticsSearch): EdyStatisticsView => {
  const views = edyStatisticsTabViews[search.tab]
  return views.find((view) => view === search.view) ?? views[0] ?? 'daily'
}

export type EdyStatisticsPeriod = {
  year: number
  month: number
  date: string
}

const clampDate = (date: string, today: string) => {
  if (date < hourlyStatisticsStartDate) {
    return hourlyStatisticsStartDate
  }
  return date > today ? today : date
}

export const resolveStatisticsPeriod = (
  search: EdyStatisticsSearch,
  today: string
): EdyStatisticsPeriod => {
  const currentYear = Number(today.slice(0, 4))
  const currentMonth = Number(today.slice(5, 7))
  const year = Math.min(search.year ?? currentYear, currentYear)
  const month = search.month ?? currentMonth

  return {
    year,
    month: year === currentYear ? Math.min(month, currentMonth) : month,
    date: clampDate(search.date ?? today, today),
  }
}

export const statisticsYears = (today: string) => {
  const currentYear = Number(today.slice(0, 4))
  return Array.from(
    { length: currentYear - statisticsStartYear + 1 },
    (_, index) => currentYear - index
  )
}
